import {orderBy} from "lodash"
import {RecurringPaymentStatus, StudentRecurringPayment} from "types/student-account/student-payment"
import {PaymentPlanItem} from "types/student-account/payment-plan"

export const hasActiveRecurringPaymentForPlan = (
    recurringPayments: StudentRecurringPayment[],
    paymentPlanId?: number | null
): boolean => {
    if (!paymentPlanId) return false
    return recurringPayments.some(
        (payment) =>
            payment.status === RecurringPaymentStatus.Active &&
            payment.paymentPlanItems?.some((item) => item.paymentPlanId === paymentPlanId)
    )
}

// Unlike hasActiveRecurringPaymentForPlan (true for every item on a plan that has any active
// recurring payment), this only matches the specific item(s) a recurring payment is currently
// linked to and about to bill next — used to show "In Progress" on just the upcoming charge
// instead of every unpaid item on the plan.
export const isPaymentPlanItemUpNextForRecurring = (
    recurringPayments: StudentRecurringPayment[],
    paymentPlanItemId?: number | null
): boolean => {
    if (!paymentPlanItemId) return false
    return recurringPayments.some(
        (payment) =>
            payment.status === RecurringPaymentStatus.Active &&
            payment.paymentPlanItems?.some((item) => item.paymentPlanItemId === paymentPlanItemId)
    )
}

export type RecurringPaymentPlanItemLink = {
    paymentPlanId: number
    paymentPlanItemId: number
    accountId?: number
    amount: number
}

// A recurring subscription pays down one payment plan item per billing cycle — edular-core
// auto-advances it to the next unpaid item after each successful charge (see
// advanceRecurringPaymentPlanItemsIfFullyPaid on the backend). So no matter how many items the
// student had checked in the payment modal, a NEW subscription must only ever link the single
// earliest-due unpaid one, at its own remaining balance — never the whole selection at the
// (unrelated) total/per-cycle amount.
//
// Always resolved against the *plan's* full item list, not just what the student happened to
// select — a student can check a later installment without checking an earlier unpaid one, and
// the subscription must still start from the plan's true earliest-due unpaid item (matching
// getRecurringPaymentFromPaymentPlan) rather than skip the earlier balance entirely.
export const buildRecurringPaymentPlanItemsPayload = (
    selectedPaymentItems: PaymentPlanItem[] = []
): RecurringPaymentPlanItemLink[] => {
    // The back-reference to the parent plan (and its accountId) is only attached to the flat
    // "selected items" list built for the payment modal — items nested inside plan.items itself
    // don't carry it, so it's captured once here rather than read off whichever item ends up
    // being picked.
    const plan = selectedPaymentItems[0]?.paymentPlan
    const planItems = plan?.items?.length ? plan.items : selectedPaymentItems

    const [nextItem] = orderBy(
        planItems.filter((item) => item.paidAmount < item.amount),
        ["dueDate"],
        ["asc"]
    )
    if (!nextItem) return []
    return [
        {
            paymentPlanId: nextItem.paymentPlanId,
            paymentPlanItemId: nextItem.paymentPlanItemId,
            accountId: nextItem.paymentAccountId ?? plan?.accountId,
            amount: nextItem.amount - nextItem.paidAmount
        }
    ]
}
